'use client';

import { Canvas } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import styles from './GlobeIndicator.module.css';
import { useIntroDriver } from './intro';
import { type GeoNode, HOME, useMe } from './location';
import { GlobeScene, staticDriver } from './scene';
import { utcOffsetLabel } from './utcOffsetLabel';

function Stage({
  me,
  animated,
  onReady,
}: {
  me: GeoNode;
  animated: boolean;
  onReady: () => void;
}) {
  const introDriver = useIntroDriver({ node: me, onConnected: onReady });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!animated) onReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once on mount when there is no intro animation
  }, []);
  return (
    <GlobeScene node={me} driver={animated ? introDriver : staticDriver} />
  );
}

export const GlobeIndicator = ({
  onConnected = () => {},
  fill = false,
  animated = false,
}: {
  onConnected?: () => void;
  fill?: boolean;
  animated?: boolean;
}) => {
  const { me } = useMe();
  const homeOffset = useMemo(() => utcOffsetLabel(HOME.tz), []);
  const awayFromHome = !me.city.startsWith(HOME.city);

  return (
    <div className={`${styles.wrap} ${fill ? styles.fill : ''}`}>
      <div className={styles.canvasBox}>
        <Canvas
          orthographic={true}
          camera={{ position: [0, 0, 10], zoom: 140, near: 0.01, far: 100 }}
          gl={{ alpha: true, antialias: true }}
          dpr={[1, 2]}
        >
          <Stage me={me} animated={animated} onReady={onConnected} />
        </Canvas>
      </div>
      <div className={styles.caption}>
        {homeOffset} <b>{HOME.city}</b>
        {awayFromHome && (
          <span style={{ display: 'block', opacity: 0.6 }}>
            now in {me.city}
          </span>
        )}
      </div>
    </div>
  );
};
